import React, { useState, useEffect } from "react";
import styled from "styled-components";
import anniversaryBackground from "../assets/video/anniversary-background.mp4";
import logoImage from "../assets/images/logos/40th-color.png";
import sanityClient from "../sanityClient";

// Styled Components
const Container = styled.div`
  position: relative;
  color: #fff;
  text-align: left;
  overflow: hidden;
  min-height: 70vh;
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -2;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6); /* Dark overlay */
  z-index: -1;
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  flex-wrap: wrap;
  padding: 50px 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const ImageContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const StyledImage = styled.img`
  max-width: 90%;
  border-radius: 10px;
  background: none; /* Ensure transparency */
  filter: drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.2)); /* Add a lift effect */
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px); /* Slight lift on hover */
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TextContent = styled.div`
  flex: 1;
  padding: 0 30px;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const Title = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 15px;
  color: #fff;
`;

const Description = styled.div`
  font-size: 1.2rem;
  color: #ddd;
  line-height: 1.8;
  margin-bottom: 30px;
  text-align: left;

  ul {
    padding-left: 20px;
    margin-top: 10px;
    text-align: left;

    li {
      margin-bottom: 10px;
    }

    @media (max-width: 768px) {
      text-align: left;
      padding-left: 20px;
    }
  }
`;

const ButtonContainer = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const ActionButton = styled.a`
  display: block;
  background-color: #ff9800;
  color: #fff;
  padding: 15px 0;
  border-radius: 5px;
  font-size: 1.2rem;
  font-weight: bold;
  text-decoration: none;
  text-align: center;
  width: 100%;
  max-width: 400px; /* Matches the max width of the progress bar */
  margin: 0 auto;
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: #e57c00;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 100%; /* Adjust to screen size */
  }
`;

const ProgressContainer = styled.div`
  margin-top: 30px;
  text-align: center;
`;

const ProgressTitle = styled.h3`
  color: #fff;
  margin-bottom: 10px;
  font-size: 18px;
`;

const ProgressBar = styled.div`
  background: #ccc;
  border-radius: 10px;
  overflow: hidden;
  height: 25px;
  width: 100%;
  max-width: 400px; /* Consistent with the button */
  margin: 0 auto;
`;

const ProgressBarFill = styled.div`
  background: #ff9800;
  height: 100%;
  transition: width 1.5s ease;
`;

const ProgressText = styled.p`
  font-size: 16px;
  color: #ddd;
`;

const AnniversaryHighlight = () => {
  const [progress, setProgress] = useState(0);
  const [fundsRaised, setFundsRaised] = useState(0);
  const fundraisingGoal = 100000;

  useEffect(() => {
    // Fetch data from Sanity
    sanityClient
      .fetch(
        `*[_type == "fundraisingProgress"][0]{
          currentAmount
        }`
      )
      .then((data) => {
        const amountRaised = data.currentAmount || 0; // Fallback to 0 if no data
        setFundsRaised(amountRaised);
        const progressPercentage = (amountRaised / fundraisingGoal) * 100;
        setProgress(progressPercentage);
      })
      .catch((error) =>
        console.error("Error fetching data from Sanity:", error)
      );
  }, []);

  return (
    <Container>
      <Background>
        <video autoPlay loop muted playsInline>
          <source src={anniversaryBackground} type="video/mp4" />
        </video>
      </Background>
      <Overlay />
      <Content>
        <ImageContent>
          <StyledImage src={logoImage} alt="40th Anniversary Logo" />
        </ImageContent>
        <TextContent>
          <Title>Celebrating 40 Years of Habitat Buffalo</Title>
          <Description>
            Join us as we celebrate four decades of building hope and homes in
            Buffalo. This year’s highlights include:
            <ul>
              <li>A trailblazing sign to honor our founders.</li>
              <li>A mural dedication at our ReStore.</li>
              <li>A community celebration to bring everyone together.</li>
              <li>A telethon to raise support for our mission.</li>
              <li>
                And finally, a grand gala to commemorate 40 years of impact.
              </li>
            </ul>
            Plus, our #400in40 campaign aims to raise $100,000 to fund our 400th
            house for a family in need. Get involved and help us make history!
          </Description>
          <ButtonContainer>
            <ActionButton href="/anniversary" rel="noopener noreferrer">
              Learn More and Get Involved
            </ActionButton>
          </ButtonContainer>
          <ProgressContainer>
            <ProgressTitle>#400in40 Fundraising Progress</ProgressTitle>
            <ProgressBar>
              <ProgressBarFill style={{ width: `${progress}%` }} />
            </ProgressBar>
            <ProgressText>
              We've raised ${fundsRaised.toLocaleString()} so far,{" "}
              {Math.round(progress)}% of our $100,000 goal to fund the 400th
              house!
            </ProgressText>
          </ProgressContainer>
        </TextContent>
      </Content>
    </Container>
  );
};

export default AnniversaryHighlight;
